/**
 * Task hooks for React Query
 *
 * Provides data fetching and mutation hooks for task management.
 * Uses the real Task Manager API via BFF.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TaskFilters, CreateTaskInput, UpdateTaskInput, TaskPriority } from '@/types';
import * as tasksApi from '@/api/tasks';

// Query options type
export interface TaskQueryOptions {
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Query keys - include options in key for proper cache invalidation
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters?: TaskFilters, options?: TaskQueryOptions) => 
    [...taskKeys.lists(), { filters, options }] as const,
  detail: (id: string) => [...taskKeys.all, 'detail', id] as const,
  today: () => [...taskKeys.all, 'today'] as const,
  week: () => [...taskKeys.all, 'week'] as const,
  overdue: () => [...taskKeys.all, 'overdue'] as const,
  focus: () => [...taskKeys.all, 'focus'] as const,
  inbox: () => [...taskKeys.all, 'inbox'] as const,
  stats: () => [...taskKeys.all, 'stats'] as const,
  values: (taskId: string) => [...taskKeys.all, 'values', taskId] as const,
  goals: (taskId: string) => [...taskKeys.all, 'goals', taskId] as const,
};

/**
 * Hook for listing tasks with filters
 */
export function useTasks(
  filters?: TaskFilters,
  options?: TaskQueryOptions
) {
  return useQuery({
    queryKey: taskKeys.list(filters, options),
    queryFn: () => tasksApi.listTasks(filters, options),
    // Mark data as stale immediately to ensure fresh data on navigation
    staleTime: 0,
    // Refetch when window regains focus or component mounts
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
}

/**
 * Hook for getting a single task
 */
export function useTask(taskId: string) {
  return useQuery({
    queryKey: taskKeys.detail(taskId),
    queryFn: () => tasksApi.getTask(taskId),
    enabled: Boolean(taskId),
  });
}

/**
 * Hook for getting tasks due today
 */
export function useTasksToday() {
  return useQuery({
    queryKey: taskKeys.today(),
    queryFn: () => tasksApi.getTasksToday(),
  });
}

/**
 * Hook for getting tasks due this week
 */
export function useTasksWeek() {
  return useQuery({
    queryKey: taskKeys.week(),
    queryFn: () => tasksApi.getTasksWeek(),
  });
}

/**
 * Hook for getting overdue tasks
 */
export function useTasksOverdue() {
  return useQuery({
    queryKey: taskKeys.overdue(),
    queryFn: () => tasksApi.getTasksOverdue(),
  });
}

/**
 * Hook for getting focused tasks
 */
export function useTasksFocus() {
  return useQuery({
    queryKey: taskKeys.focus(),
    queryFn: () => tasksApi.getTasksFocus(),
    // Refetch on window focus for fresh focus data
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for getting inbox (unverified) tasks for triage
 */
export function useTasksInbox() {
  return useQuery({
    queryKey: taskKeys.inbox(),
    queryFn: () => tasksApi.getTasksInbox(),
    // Refetch on window focus for fresh inbox
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for getting task statistics
 */
export function useTaskStats() {
  return useQuery({
    queryKey: taskKeys.stats(),
    queryFn: () => tasksApi.getTaskStats(),
  });
}

/**
 * Hook for task mutations (create, update, delete, complete, reopen)
 */
export function useTaskMutations() {
  const queryClient = useQueryClient();

  // Invalidate all task queries
  const invalidateTasks = () => {
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
  };

  // Create task
  const createTask = useMutation({
    mutationFn: (input: CreateTaskInput) => tasksApi.createTask(input),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Update task
  const updateTask = useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input: UpdateTaskInput }) =>
      tasksApi.updateTask(taskId, input),
    onMutate: async ({ taskId, input }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: taskKeys.detail(taskId) });

      // Snapshot previous value
      const previousTask = queryClient.getQueryData(taskKeys.detail(taskId));

      // Optimistically update
      queryClient.setQueryData(taskKeys.detail(taskId), (old: { task: Record<string, unknown> } | undefined) => {
        if (!old || !old.task) return old;
        return {
          task: { ...old.task, ...input },
        };
      });

      return { previousTask };
    },
    onError: (_err, { taskId }, context) => {
      // Rollback on error
      if (context?.previousTask) {
        queryClient.setQueryData(taskKeys.detail(taskId), context.previousTask);
      }
    },
    onSettled: () => {
      invalidateTasks();
    },
  });

  // Delete task
  const deleteTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.deleteTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Complete task
  const completeTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.completeTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Reopen task
  const reopenTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.reopenTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Reschedule task
  const rescheduleTask = useMutation({
    mutationFn: ({ taskId, dueAt }: { taskId: string; dueAt: string | null }) =>
      tasksApi.rescheduleTask(taskId, dueAt),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Set priority
  const setPriority = useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: TaskPriority }) =>
      tasksApi.setTaskPriority(taskId, priority),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Dismiss task (soft delete - won't come back after sync)
  const dismissTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.dismissTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Convert to reference (not a task, but a notable)
  const convertToReference = useMutation({
    mutationFn: (taskId: string) => tasksApi.convertToReference(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Focus task
  const focusTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.focusTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Unfocus task
  const unfocusTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.unfocusTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Toggle focus
  const toggleFocus = useMutation({
    mutationFn: ({ taskId, isFocus }: { taskId: string; isFocus: boolean }) =>
      tasksApi.toggleFocus(taskId, isFocus),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Set task values (values this task supports)
  const setTaskValues = useMutation({
    mutationFn: ({ taskId, valueIds, source = 'manual' }: { 
      taskId: string; 
      valueIds: string[];
      source?: string;
    }) => tasksApi.setTaskValues(taskId, valueIds, source),
    onSuccess: (_, { taskId }) => {
      // Invalidate task values and general task queries
      queryClient.invalidateQueries({ queryKey: taskKeys.values(taskId) });
      queryClient.invalidateQueries({ queryKey: ['values', 'supportingTasks'] });
      invalidateTasks();
    },
  });

  // Set task goals (goals this task supports/advances)
  const setTaskGoals = useMutation({
    mutationFn: ({ taskId, goalIds, source = 'manual' }: { 
      taskId: string; 
      goalIds: string[];
      source?: string;
    }) => tasksApi.setTaskGoals(taskId, goalIds, source),
    onSuccess: (_, { taskId }) => {
      // Invalidate task goals and goal-related queries
      queryClient.invalidateQueries({ queryKey: taskKeys.goals(taskId) });
      queryClient.invalidateQueries({ queryKey: ['goals', 'supportingTasks'] });
      invalidateTasks();
    },
  });

  // Verify task (move from inbox to active tasks)
  const verifyTask = useMutation({
    mutationFn: (taskId: string) => tasksApi.verifyTask(taskId),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Bulk verify multiple tasks
  const bulkVerify = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.bulkVerifyTasks(taskIds),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Bulk dismiss multiple tasks
  const bulkDismiss = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.bulkDismissTasks(taskIds),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  return {
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    reopenTask,
    rescheduleTask,
    setPriority,
    dismissTask,
    convertToReference,
    focusTask,
    unfocusTask,
    toggleFocus,
    setTaskValues,
    setTaskGoals,
    verifyTask,
    bulkVerify,
    bulkDismiss,
  };
}

// =============================================================================
// TASK VALUES (Values a task supports)
// =============================================================================

/**
 * Hook to get values that a task supports (serves)
 */
export function useTaskValues(taskId: string) {
  return useQuery({
    queryKey: taskKeys.values(taskId),
    queryFn: () => tasksApi.getTaskValues(taskId),
    enabled: !!taskId,
  });
}

// =============================================================================
// TASK GOALS (Goals a task supports/advances)
// =============================================================================

/**
 * Hook to get goals that a task supports (advances)
 */
export function useTaskGoals(taskId: string) {
  return useQuery({
    queryKey: taskKeys.goals(taskId),
    queryFn: () => tasksApi.getTaskGoals(taskId),
    enabled: !!taskId,
  });
}
